/*
  CTA collection pages (refined static-collection).
  Filters, sort and pagination swap the page's named regions in place through the Section Rendering
  API (?section_id=), with history entries, a response cache and hover / focus / next-page prefetch.
  Family chips (another collection) do the same from the full page, header and About band included.
  Any failure falls back to a normal navigation. Also: tag counts for the filter groups
  (sections/cta-collection-facets.liquid), the floating Filters / Sort pill and its sheets
  (focus trap, Escape, focus return, scroll lock), Read more, scroll reveal and a live region.
  Same model as cta-search-page.js.
*/
(() => {
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const desktop = () => window.matchMedia('(min-width: 1080px)').matches;
  const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

  // ---------- Read more (header intro and the About band, which sits outside the page root) ----------
  const bindMore = (scope) => {
    scope.querySelectorAll('[data-cta-col-more]').forEach((button) => {
      if (button.dataset.ctaReady) return;
      button.dataset.ctaReady = 'true';
      const box = button.closest('.cta-col__intro, .cta-col-about__body');
      const text = document.getElementById(button.getAttribute('aria-controls'));
      if (!box || !text) return;
      const label = button.querySelector('[data-cta-col-more-label]');

      // Text that fits needs no button; hidden without moving anything
      const fits = () => !box.classList.contains('is-open') && text.scrollHeight <= text.clientHeight + 2;
      const check = () => {
        if (getComputedStyle(button).display === 'none') return;
        button.style.visibility = fits() ? 'hidden' : '';
      };
      check();
      if (document.fonts?.ready) document.fonts.ready.then(check);

      // Height tween between the clamped and full text (GSAP when loaded, else Web Animations)
      const still = () =>
        window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
        document.body.hasAttribute('data-reduce-animations') ||
        Boolean(window.Shopify && window.Shopify.designMode);
      const tween = (from, to, done) => {
        text.style.height = `${from}px`;
        text.style.overflow = 'hidden';
        const finish = () => {
          text.style.height = '';
          text.style.overflow = '';
          done();
        };
        if (window.gsap) {
          window.gsap.to(text, { height: to, duration: 0.45, ease: 'power3.inOut', overwrite: true, onComplete: finish });
        } else {
          text.animate([{ height: `${from}px` }, { height: `${to}px` }], { duration: 450, easing: 'cubic-bezier(.65,0,.35,1)' })
            .finished.then(finish, finish);
        }
      };
      const setLabel = (open) => {
        button.setAttribute('aria-expanded', String(open));
        if (!label) return;
        if (still() || !label.animate) {
          label.textContent = open ? 'Show less' : 'Read more';
          return;
        }
        label.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 120 }).finished.then(() => {
          label.textContent = open ? 'Show less' : 'Read more';
          label.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 160 });
        });
      };
      let busy = false;

      button.addEventListener('click', () => {
        if (busy) return;
        const open = !box.classList.contains('is-open');
        setLabel(open);
        if (still()) {
          box.classList.toggle('is-open', open);
          return;
        }
        busy = true;
        if (open) {
          const from = text.offsetHeight;
          box.classList.add('is-open');
          tween(from, text.scrollHeight, () => { busy = false; });
        } else {
          const from = text.offsetHeight;
          box.classList.remove('is-open');
          const to = text.offsetHeight;
          box.classList.add('is-open');
          // Keep the toggle in view when the header has scrolled away
          const top = box.getBoundingClientRect().top;
          if (top < 80) window.scrollBy({ top: top - 100, behavior: 'smooth' });
          tween(from, to, () => {
            box.classList.remove('is-open');
            busy = false;
          });
        }
      });
    });
  };

  function initPage(root) {
    if (!root || root.dataset.ctaReady) return;
    root.dataset.ctaReady = 'true';

    const sectionId = root.dataset.sectionId;
    const cache = new Map();
    const facetCache = new Map();
    let navToken = 0;

    bindMore(root);

    // The inline script centres the current family chip before fonts load; centre it again after
    // (and after a family chip swaps in a new header, whose inline script does not run)
    const centerChip = () => {
      const chips = root.querySelector('.cta-col__chips');
      const current = chips?.querySelector('[aria-current]');
      if (!current || chips.scrollWidth <= chips.clientWidth) return;
      const chip = current.getBoundingClientRect();
      const row = chips.getBoundingClientRect();
      if (chip.left >= row.left && chip.right <= row.right) return;
      chips.scrollLeft += chip.left - row.left - (row.width - chip.width) / 2;
    };
    if (document.fonts?.ready) document.fonts.ready.then(centerChip);

    // ---------- Scroll reveal ----------
    let observer = null;
    const observe = () => {
      if (!observer) return;
      root.querySelectorAll('[data-cta-srp-reveal]:not(.is-in)').forEach((el) => observer.observe(el));
    };
    if (!reduceMotion() && 'IntersectionObserver' in window && !window.Shopify?.designMode) {
      root.classList.add('cta-col--animate');
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -6% 0px', threshold: 0.06 });
      observe();
    }

    if (!sectionId) return;

    // ---------- Section fetches ----------
    const pageKey = (href) => {
      const url = new URL(href, window.location.origin);
      url.searchParams.delete('section_id');
      return url.pathname + url.search;
    };

    // Family chips lead to another collection, which may use another template: those load the whole
    // page, so the sections around this one can be checked (and the About band brought along)
    const collectionOf = (href) => (new URL(href, window.location.origin).pathname.match(/\/collections\/([^/]+)/) || [])[1] || '';
    const crosses = (href) => collectionOf(href) !== root.dataset.collection;

    const load = (href) => {
      const full = crosses(href);
      const key = (full ? 'full:' : '') + pageKey(href);
      if (!cache.has(key)) {
        const url = new URL(pageKey(href), window.location.origin);
        if (!full) url.searchParams.set('section_id', sectionId);
        const request = fetch(url, { credentials: 'same-origin' })
          .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
          .then((html) => {
            const page = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cta-col]');
            if (!page) throw new Error('No collection markup');
            if (full) {
              const host = page.closest('.shopify-section');
              page.ctaSections = host?.parentElement
                ? [...host.parentElement.children].filter((el) => el.classList.contains('shopify-section'))
                : [];
            }
            return page;
          });
        request.catch(() => cache.delete(key));
        cache.set(key, request);
      }
      return cache.get(key);
    };

    // Another collection swaps in place only when its page has the same regions and the same sections
    // around this one (the About band, which is per collection, is replaced). Returns the DOM change,
    // or throws so the caller falls back to a normal navigation.
    const isBand = (section) => section.classList.contains('cta-col-about-section') || !!section.querySelector('.cta-col-about');
    const regionNames = (scope) => [...regionsOf(scope)].map((el) => el.dataset.ctaColRegion).sort().join();
    const planSections = (page) => {
      if (regionNames(page) !== regionNames(root)) throw new Error('Different layout');
      const host = root.closest('.shopify-section');
      const parent = host?.parentElement;
      if (!parent || !page.ctaSections?.length) throw new Error('No sections');
      const live = [...parent.children].filter((el) => el.classList.contains('shopify-section'));
      const byId = new Map(live.map((section) => [section.id, section]));
      const nodes = page.ctaSections.map((section) => {
        if (section.contains(page)) return host;
        if (isBand(section)) return document.importNode(section, true);
        const same = byId.get(section.id);
        if (same && !isBand(same)) return same;
        throw new Error('Different sections');
      });
      if (live.some((section) => !nodes.includes(section) && !isBand(section))) throw new Error('Different sections');
      return () => {
        live.forEach((section) => { if (!nodes.includes(section)) section.remove(); });
        let cursor = [...parent.children].find((el) => el.classList.contains('shopify-section')) || null;
        nodes.forEach((node) => {
          if (node !== cursor) parent.insertBefore(node, cursor);
          cursor = node.nextElementSibling;
          if (isBand(node)) bindMore(node);
        });
      };
    };

    cache.set(pageKey(window.location.href), Promise.resolve(root.cloneNode(true)));

    // ---------- Filter counts ----------
    // One request per distinct state (the current one, and the state without each group's active tag).
    const loadFacets = (href) => {
      const key = pageKey(href);
      if (!facetCache.has(key)) {
        const fetchPage = (page) => {
          const url = new URL(key, window.location.origin);
          url.searchParams.set('section_id', 'cta-collection-facets');
          if (page > 1) url.searchParams.set('page', page);
          return fetch(url, { credentials: 'same-origin' })
            .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
            .then((html) => {
              const json = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cta-col-facets]');
              if (!json) throw new Error('No facets');
              return JSON.parse(json.textContent);
            });
        };
        const request = fetchPage(1).then((first) => {
          const pages = Math.min(first.pages || 1, 8);
          const rest = [];
          for (let page = 2; page <= pages; page += 1) rest.push(fetchPage(page));
          return Promise.all(rest).then((more) => {
            const counts = new Map();
            [first, ...more].forEach((data) => {
              (data.tags || []).forEach(([handle, n]) => counts.set(handle, (counts.get(handle) || 0) + n));
            });
            return counts;
          });
        });
        request.catch(() => facetCache.delete(key));
        facetCache.set(key, request);
      }
      return facetCache.get(key);
    };

    const applyCounts = (scope, counts, url) => {
      scope.querySelectorAll(`[data-count-url="${CSS.escape(url)}"] [data-cta-col-option]`).forEach((option) => {
        const handle = option.dataset.ctaColOption.replace(/\|/g, '');
        const n = counts.get(handle) || 0;
        const slot = option.querySelector('[data-cta-col-count]');
        if (slot) slot.textContent = n;
        const active = option.classList.contains('is-active');
        option.classList.toggle('is-disabled', n === 0 && !active);
        if (n === 0 && !active) option.setAttribute('aria-disabled', 'true');
        else option.removeAttribute('aria-disabled');
      });
    };

    // Fills every group of a page (live, or a fetched one before it is swapped in)
    const fillCounts = (scope) => {
      const urls = [...new Set([...scope.querySelectorAll('[data-count-url]')].map((group) => group.dataset.countUrl))];
      return Promise.all(urls.map((url) => loadFacets(url)
        .then((counts) => applyCounts(scope, counts, url))
        .catch(() => {})));
    };

    // ---------- In-place navigation ----------
    const status = root.querySelector('[data-cta-col-status]');
    const regionsOf = (scope) => scope.querySelectorAll('[data-cta-col-region]');

    // Room for the fixed site header above a scroll target, and the band it covers when it slides in
    const headerOffset = () => (desktop() ? 96 : 72);
    const siteHeader = document.querySelector('.site-header');
    const headerSpace = () => siteHeader?.offsetHeight || 0;

    // Pagination brings the top of the results into view first, and waits for the scroll to finish
    const scrollToResults = () => new Promise((resolve) => {
      const target = root.querySelector('[data-cta-col-body]') || root;
      const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - headerOffset());
      if (window.scrollY <= top) {
        resolve();
        return;
      }
      const smooth = !reduceMotion();
      window.scrollTo({ top, behavior: smooth ? 'smooth' : 'auto' });
      if (!smooth) {
        resolve();
        return;
      }
      let last = -1;
      let still = 0;
      const started = performance.now();
      const tick = () => {
        const y = window.scrollY;
        still = Math.abs(y - last) < 1 ? still + 1 : 0;
        last = y;
        if (still >= 3 || performance.now() - started > 900) resolve();
        else requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });

    // Results that come back shorter keep enough height that nothing below them slides into view
    // (all of their old height when their bottom edge was on screen), until the visitor's next input.
    let heldResults = null;
    const releaseHold = () => {
      if (!heldResults) return;
      heldResults.style.minHeight = '';
      heldResults = null;
    };
    window.addEventListener('pointerdown', releaseHold, { passive: true });
    window.addEventListener('keydown', releaseHold);

    const swapResults = (live, incoming) => {
      releaseHold();
      const before = live.getBoundingClientRect();
      const keep = before.bottom <= window.innerHeight
        ? before.height
        : Math.min(before.height, Math.ceil(window.innerHeight - before.top));
      live.replaceChildren(...[...incoming.childNodes].map((node) => node.cloneNode(true)));
      if (keep > 0 && live.offsetHeight < keep) {
        live.style.minHeight = `${keep}px`;
        heldResults = live;
      }
    };

    // Group open / closed state, by region and group name, so a swap keeps groups as the visitor left them
    const groupKey = (group) => `${group.closest('[data-cta-col-region]')?.dataset.ctaColRegion}:${group.querySelector('.cta-col__group-name')?.textContent.trim()}`;
    const rememberGroups = () => new Map([...root.querySelectorAll('.cta-col__group')].map((group) => [groupKey(group), group.open]));
    const restoreGroups = (state) => {
      root.querySelectorAll('.cta-col__group').forEach((group) => {
        const open = state.get(groupKey(group));
        if (open !== undefined) group.open = open;
      });
    };

    // Rendered and visible (the pill counts as hidden as soon as it starts to hide)
    const shown = (el) => !!el && el.isConnected && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden'
      && !el.closest('[data-cta-col-fab]:not(.is-visible)');
    const resultsHeading = () => root.querySelector('[data-cta-col-results] h2[tabindex]');

    // Where focus goes when the control that had it is gone after a swap (a removed pill, Clear all):
    // inside an open sheet it stays in the sheet; elsewhere the next pill, then the Products heading.
    const focusFallback = (region) => {
      if (openSheet) {
        return ['.cta-col__pill', '[data-key="show"]', '.cta-col__close']
          .map((selector) => openSheet.querySelector(selector))
          .find(shown);
      }
      if (region === 'bar' || region === 'results') {
        const pill = [...root.querySelectorAll(`[data-cta-col-region="${region}"] .cta-col__pill`)].find(shown);
        if (pill) return pill;
      }
      return resultsHeading();
    };

    const render = (page) => {
      // Focus inside a region that is about to be replaced comes back to its twin, or to a fallback
      const focused = document.activeElement;
      const focusRegion = focused?.closest?.('[data-cta-col-region]')?.dataset.ctaColRegion || null;
      const focusKey = focusRegion ? focused.dataset.key || '' : null;
      const groups = rememberGroups();
      const before = { before: parseInt(root.dataset.count, 10) || 0, activeBefore: parseInt(root.dataset.active, 10) || 0 };

      // Another collection: its header (title, intro, image, family chips) comes along
      if (page.dataset.collection !== root.dataset.collection) {
        const head = root.querySelector('[data-cta-col-head]');
        const incomingHead = page.querySelector('[data-cta-col-head]');
        if (head && incomingHead) {
          const chipKey = head.contains(focused) ? focused.dataset.key : null;
          head.replaceChildren(...[...incomingHead.childNodes].map((node) => document.importNode(node, true)));
          bindMore(head);
          centerChip();
          if (chipKey) head.querySelector(`[data-key="${CSS.escape(chipKey)}"]`)?.focus({ preventScroll: true });
        }
        root.dataset.collection = page.dataset.collection;
        ['cta-col--filters', 'cta-col--sort'].forEach((name) => root.classList.toggle(name, page.classList.contains(name)));
      }

      root.dataset.count = page.dataset.count;
      root.dataset.active = page.dataset.active;
      root.dataset.title = page.dataset.title;

      regionsOf(root).forEach((live) => {
        const name = live.dataset.ctaColRegion;
        const incoming = page.querySelector(`[data-cta-col-region="${name}"]`);
        if (!incoming) return;
        if (name === 'results') {
          swapResults(live, incoming);
        } else {
          live.replaceChildren(...[...incoming.childNodes].map((node) => node.cloneNode(true)));
        }
      });

      restoreGroups(groups);

      const badge = root.querySelector('[data-cta-col-badge]');
      const active = parseInt(page.dataset.active, 10) || 0;
      if (badge) {
        badge.textContent = active;
        badge.hidden = active === 0;
      }

      // The title arrives HTML-escaped (translations escape their quotes); a textarea decodes it as text
      if (page.dataset.title) {
        const decoder = document.createElement('textarea');
        decoder.innerHTML = page.dataset.title;
        document.title = decoder.value.replace(/\s+/g, ' ').trim();
      }

      const count = parseInt(page.dataset.count, 10) || 0;
      if (status) status.textContent = `Showing ${plural(count, 'product', 'products')}`;

      if (focusRegion && !root.contains(document.activeElement)) {
        const twin = focusKey ? root.querySelector(`[data-key="${CSS.escape(focusKey)}"]`) : null;
        (shown(twin) ? twin : focusFallback(focusRegion))?.focus({ preventScroll: true });
      }

      observe();
      watchNextPage();
      updateFab();
      root.dispatchEvent(new CustomEvent('cta-col:render', { detail: { ...before, after: count, activeAfter: active } }));
    };

    const navigate = (href, { push = true, isPage = false } = {}) => {
      const token = ++navToken;
      root.classList.add('is-loading');
      root.querySelector('[data-cta-col-results]')?.setAttribute('aria-busy', 'true');
      return load(href)
        .then((page) => (isPage ? scrollToResults().then(() => page) : page))
        .then((page) => {
          if (token !== navToken) return;
          if (page.dataset.collection !== root.dataset.collection) planSections(page)();
          render(page);
          fillCounts(root);
          if (openSheet) sheetChanged = true;
          if (push) window.history.pushState({ ctaCol: true }, '', pageKey(href));
          if (isPage) {
            const heading = root.querySelector('[data-cta-col-results] h2[tabindex]');
            if (heading) heading.focus({ preventScroll: true });
          }
        })
        .catch(() => {
          window.location.href = href;
        })
        .finally(() => {
          if (token !== navToken) return;
          root.classList.remove('is-loading');
          root.querySelector('[data-cta-col-results]')?.removeAttribute('aria-busy');
        });
    };

    root.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('[data-cta-col-nav]');
      if (!link || !root.contains(link) || link.target === '_blank') return;
      event.preventDefault();
      if (link.getAttribute('aria-disabled') === 'true') return;
      const isPage = link.hasAttribute('data-cta-col-page');
      if (link.hasAttribute('data-cta-col-sort-option')) closeSheet();
      navigate(link.href, { isPage });
    });

    // Warm the cache when a pointer or focus heads for a filter, pill or page link
    const warm = (event) => {
      const link = event.target.closest?.('[data-cta-col-nav]');
      if (link && root.contains(link) && link.getAttribute('aria-disabled') !== 'true' && !link.hasAttribute('data-cta-col-sort-option')) {
        load(link.href).then(fillCounts).catch(() => {});
      }
    };
    root.addEventListener('pointerover', warm);
    root.addEventListener('focusin', warm);

    // The next page loads once the pagination comes into view
    let pageObserver = null;
    function watchNextPage() {
      if (!('IntersectionObserver' in window)) return;
      pageObserver?.disconnect();
      const next = root.querySelector('[data-cta-col-next]');
      if (!next) return;
      pageObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        pageObserver.disconnect();
        load(next.href).catch(() => {});
      }, { rootMargin: '400px 0px' });
      pageObserver.observe(next);
    }
    watchNextPage();

    root.addEventListener('change', (event) => {
      const form = event.target.closest('[data-cta-col-sort]');
      if (!form) return;
      const url = new URL(form.action, window.location.origin);
      new FormData(form).forEach((value, key) => url.searchParams.append(key, value));
      navigate(url.toString());
    });

    window.history.replaceState({ ctaCol: true }, '', window.location.href);
    window.addEventListener('popstate', (event) => {
      if (event.state?.ctaCol) navigate(window.location.href, { push: false });
    });

    // ---------- Sheets (filters, sort) ----------
    let openSheet = null;
    let opener = null;
    let sheetChanged = false;
    const focusables = (el) => [...el.querySelectorAll('a[href], button:not([disabled]), summary, select, [tabindex]:not([tabindex="-1"])')]
      .filter((node) => node.offsetParent !== null || node === document.activeElement);

    function closeSheet() {
      if (!openSheet) return;
      const sheet = openSheet;
      openSheet = null;
      document.documentElement.classList.remove('cta-col-locked');

      // Filters changed while the sheet was open: results that now end above the screen (they shrank
      // under a visitor who had scrolled down) come back into view, behind the closing sheet
      if (sheetChanged) {
        sheetChanged = false;
        releaseHold();
        const body = root.querySelector('[data-cta-col-body]');
        const box = body?.getBoundingClientRect();
        if (box && (box.top < 0 || box.top > window.innerHeight)) {
          window.scrollTo({ top: Math.max(0, box.top + window.scrollY - headerOffset()), behavior: 'auto' });
        }
      }
      updateFab();

      const finish = () => {
        sheet.hidden = true;
        sheet.classList.remove('is-open', 'is-closing');
      };
      if (reduceMotion()) {
        finish();
      } else {
        sheet.classList.add('is-closing');
        window.setTimeout(finish, 240);
      }
      (shown(opener) ? opener : resultsHeading())?.focus({ preventScroll: true });
    }

    const open = (name, trigger) => {
      const sheet = root.querySelector(`[data-cta-col-sheet="${name}"]`);
      if (!sheet) return;
      closeSheet();
      opener = trigger;
      openSheet = sheet;
      sheetChanged = false;
      sheet.hidden = false;
      sheet.classList.remove('is-closing');
      sheet.classList.add('is-open');
      document.documentElement.classList.add('cta-col-locked');
      const first = sheet.querySelector('.cta-col__group[open] .cta-col__option, [aria-current="true"], .cta-col__close');
      (first || sheet.querySelector('[data-cta-col-close]'))?.focus({ preventScroll: true });
    };

    root.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-cta-col-open]');
      if (trigger) {
        open(trigger.dataset.ctaColOpen, trigger);
        return;
      }
      const close = event.target.closest('[data-cta-col-close]');
      if (close && openSheet && openSheet.contains(close)) {
        if (close.getAttribute('aria-disabled') === 'true') return;
        closeSheet();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (!openSheet) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeSheet();
        return;
      }
      if (event.key !== 'Tab') return;
      const nodes = focusables(openSheet.querySelector('[data-cta-col-panel]'));
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !openSheet.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    });

    // Leaving the phone layout with a sheet open closes it
    window.matchMedia('(min-width: 1080px)').addEventListener?.('change', () => {
      if (desktop()) closeSheet();
    });

    // ---------- Floating pill ----------
    // It is sticky at the end of the results body, so it floats at the bottom of the screen over the
    // results and docks under them when they end; it never covers the help band. Shown once the chips
    // (or the header) have scrolled away or under the site header, or whenever its docked place is on
    // screen (short results).
    const fab = root.querySelector('[data-cta-col-fab]');
    let fabFrame = 0;
    function updateFab() {
      fabFrame = 0;
      if (!fab) return;
      const anchor = root.querySelector('.cta-col__family') || root.querySelector('.cta-col__head');
      const body = root.querySelector('[data-cta-col-body]');
      if (!anchor || !body) return;
      const box = body.getBoundingClientRect();
      const onScreen = box.top < window.innerHeight && box.bottom > 0;
      const pastAnchor = anchor.getBoundingClientRect().bottom <= headerSpace();
      fab.classList.toggle('is-visible', onScreen && (pastAnchor || box.bottom <= window.innerHeight));
    }
    if (fab) {
      const queue = () => {
        if (!fabFrame) fabFrame = requestAnimationFrame(updateFab);
      };
      window.addEventListener('scroll', queue, { passive: true });
      window.addEventListener('resize', queue);
      updateFab();
    }

    // Counts for groups the server couldn't compute
    const idle = window.requestIdleCallback || ((fn) => window.setTimeout(fn, 200));
    if (root.querySelector('[data-count-url]')) idle(() => fillCounts(root));
  }

  const boot = () => {
    document.querySelectorAll('[data-cta-col]').forEach(initPage);
    document.querySelectorAll('.cta-col-about').forEach(bindMore);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
